import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import VegaTopBar from './VegaTopBar';
import VegaLeftPanel from './VegaLeftPanel';
import VegaCenterPanel from './VegaCenterPanel';
import VegaRightPanel from './VegaRightPanel';
import LiveTalkButton from './voice/LiveTalkButton';

export default function VegaDashboard({ onSwitchToNova, onSwitchToOrion, onSwitchToShow, onSwitchToAtlas, onSwitchToCrew }) {
  const [worldState, setWorldState] = useState(null);
  const [selectedInsightId, setSelectedInsightId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get('/api/vega/state');
      setWorldState(res.data);
      if (!selectedInsightId && res.data.insights?.length > 0) {
        const top = res.data.insights.reduce((a, b) => a.severity > b.severity ? a : b);
        setSelectedInsightId(top.id);
      }
    } catch (err) {
      console.error('Failed to fetch Vega state:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedInsightId]);

  useEffect(() => {
    fetchState();
    const interval = setInterval(fetchState, 30000);
    return () => clearInterval(interval);
  }, [fetchState]);

  const handleRefresh = useCallback(async () => {
    try {
      await api.post('/api/vega/refresh');
      await fetchState();
    } catch {}
  }, [fetchState]);

  const selectedInsight = worldState?.insights?.find(i => i.id === selectedInsightId) || null;
  const scoring = worldState?.scoring || {};

  if (loading) {
    return (
      <div className="vega-loading" data-testid="vega-loading-screen">
        <div className="vega-loading-ring"></div>
        <div className="vega-loading-text">VEGA</div>
        <div className="vega-loading-sub">Connecting to infrastructure monitoring systems...</div>
      </div>
    );
  }

  if (!worldState) {
    return (
      <div className="vega-loading" data-testid="vega-error-screen">
        <div className="vega-loading-text" style={{ background: 'linear-gradient(135deg, #EF4444, #F97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>LINK LOST</div>
        <div className="vega-loading-sub">Unable to reach Vega Brain</div>
      </div>
    );
  }

  return (
    <div className="vega-app" data-testid="vega-app">
      <VegaTopBar
        timestamp={worldState.timestamp}
        signalCount={worldState.signal_count}
        totalInsights={worldState.total_insight_count}
        state={worldState.state}
        scoring={scoring}
        sensorReadiness={worldState.sensor_readiness}
        onRefresh={handleRefresh}
        onSwitchToNova={onSwitchToNova}
        onSwitchToOrion={onSwitchToOrion}
        onSwitchToShow={onSwitchToShow}
        onSwitchToAtlas={onSwitchToAtlas}
        onSwitchToCrew={onSwitchToCrew}
        language={language}
        onLanguageChange={setLanguage}
      />
      <div className="vega-body">
        <VegaLeftPanel
          state={worldState.state}
          scoring={scoring}
          connectors={worldState.connectors}
        />
        <VegaCenterPanel
          insights={worldState.insights || []}
          offlineNotices={worldState.offlineNotices || []}
          selectedInsightId={selectedInsightId}
          onSelectInsight={setSelectedInsightId}
          scoring={scoring}
        />
        <VegaRightPanel
          insight={selectedInsight}
          language={language}
        />
      </div>
      <LiveTalkButton agent="vega" label="Talk to Vega" />
    </div>
  );
}
